import { adoptUser, getMeta, setMeta } from '../db/dexie.js'

/**
 * Thin API client. The access token is deliberately kept in memory only — the
 * refresh token lives in an httpOnly cookie the browser holds, so a stale tab
 * cannot leak a long-lived credential into storage.
 */

let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function hasSession(): boolean {
  return accessToken != null
}

async function refresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
      if (!res.ok) return false
      const body = (await res.json()) as { accessToken: string }
      accessToken = body.accessToken
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Skip the single automatic retry after refreshing an expired token. */
  noRetry?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, noRetry, headers, ...rest } = options
  const isFormData = body instanceof FormData

  const res = await fetch(path, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(isFormData ? {} : body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  })

  if (res.status === 401 && !noRetry && (await refresh())) {
    return request<T>(path, { ...options, noRetry: true })
  }

  if (!res.ok) {
    let code = 'http_error'
    let message = `${res.status} ${res.statusText}`
    try {
      const parsed = (await res.json()) as { error?: string; message?: string }
      code = parsed.error ?? code
      message = parsed.message ?? message
    } catch {
      // A non-JSON error body (a proxy timeout page) still deserves a clean throw.
    }
    throw new ApiError(res.status, code, message)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Stable per-install identifier so the server can track each device's cursor. */
export async function deviceId(): Promise<string> {
  const existing = await getMeta<string | null>('deviceId', null)
  if (existing) return existing
  const id = crypto.randomUUID()
  await setMeta('deviceId', id)
  return id
}

export async function login(email: string, password: string) {
  const res = await request<{ accessToken: string; user: { id?: string } }>('/api/auth/login', {
    method: 'POST',
    body: { email, password },
  })
  accessToken = res.accessToken
  // Before anything can sync: a device that still holds the other account's
  // diary would push it into this one.
  if (res.user?.id != null) await adoptUser(res.user.id)
  return res
}

export async function logout(): Promise<void> {
  try {
    await request('/api/auth/logout', { method: 'POST' })
  } finally {
    accessToken = null
  }
}

/** Restore a session on boot using the refresh cookie, if one is still valid. */
export async function restoreSession(): Promise<boolean> {
  return refresh()
}
